/**
 * Minimal marquee: fills one track to the viewport, then clones it once so the
 * existing `-50%` CSS keyframe loops seamlessly. The animation itself lives in
 * CSS; this only handles the DOM duplication that CSS cannot express (editor
 * blocks render a single time). No IntersectionObserver or resize listeners.
 */
class MarqueeComponent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.marqueeReady) return;

    const wrapper = this.querySelector('[ref="wrapper"]');
    const content = this.querySelector('[ref="content"]');
    const group = this.querySelector('[ref="marqueeItems[]"]');
    if (!wrapper || !content || !group) return;

    // Set synchronously so a re-entrant connectedCallback cannot double-init
    // before the deferred build below runs.
    this.dataset.marqueeReady = 'true';

    // Fonts change text width, so measure only once they are ready to avoid a
    // re-measure/reflow after swap. Defer the whole build past first paint.
    const build = () => requestAnimationFrame(() => this.#build(wrapper, content, group));
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(build);
    } else {
      build();
    }
  }

  /**
   * @param {Element} wrapper
   * @param {Element} content
   * @param {HTMLElement} group
   */
  #build(wrapper, content, group) {
    if (!this.isConnected) return;

    // Repeat the group until a single track spans the viewport, so the loop
    // never reveals empty space on wide screens.
    let copies = 1;
    const groupWidth = group.offsetWidth;
    if (groupWidth > 0) {
      copies = Math.max(1, Math.ceil(this.offsetWidth / groupWidth));
      // Batch clone inserts into one layout invalidation.
      const fragment = document.createDocumentFragment();
      for (let i = 1; i < copies; i++) {
        fragment.appendChild(group.cloneNode(true));
      }
      content.appendChild(fragment);
    }

    // Match the previous speed feel: duration scales with the square root of the
    // number of copies. Inline style wins over the CSS fallback default.
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    this.style.setProperty('--marquee-speed', `${Math.sqrt(copies) * speedFactor}s`);

    // Duplicate the full track once for the seamless loop.
    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    wrapper.appendChild(clone);
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
